import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Bug, X, RefreshCw, Database, Cloud, HardDrive, HelpCircle, ChevronUp, ChevronDown, User, Settings, UploadCloud, Download, Wrench, Bell, LayoutGrid, Crown, Shield, Archive } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useNotifications } from '../../context/NotificationsContext';
import { usePreferences, getUserActiveModules } from '../../context/PreferencesContext';
import { loadContactsFromFile, saveContactsToFile } from '../../services/userContactsService';
import { loadPlacesFromFile, savePlacesToFile } from '../../services/userPlacesService';
import { getSubscription } from '../../services/paymentService';
import { getModulesByProfileTypeAndSubscription } from '../../services/tarifsService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://apicabaza.creago.be/rest/cabaza';

// Fields configuration for contacts
const CONTACTS_FIELDS = [
    { key: 'id', label: 'ID', color: 'text-purple-400', width: '60px' },
    { key: 'localUuid', label: 'LocalUUID', color: 'text-cyan-400', format: 'truncate', width: '80px' },
    { key: 'peopleUuid', label: 'PeopleUUID', color: 'text-blue-400', format: 'truncate', width: '80px' },
    { key: 'contactType', label: 'Type', color: 'text-yellow-400', format: 'badge', width: '80px' },
    { key: 'firstName', label: 'First', color: 'text-gray-300', width: '100px' },
    { key: 'lastName', label: 'Last', color: 'text-gray-300', width: '100px' },
    { key: 'email', label: 'Email', color: 'text-green-400', width: '150px' },
    { key: 'phone', label: 'Phone', color: 'text-gray-400', width: '100px' },
    { key: 'company', label: 'Company', color: 'text-gray-400', width: '100px' },
    { key: 'rating', label: 'Rating', color: 'text-yellow-400', width: '60px' },
    { key: 'createdAt', label: 'Created', color: 'text-gray-500', format: 'date', width: '90px' }
];

// Fields configuration for places
const PLACES_FIELDS = [
    { key: 'id', label: 'ID', color: 'text-purple-400', width: '60px' },
    { key: 'localUuid', label: 'LocalUUID', color: 'text-cyan-400', format: 'truncate', width: '80px' },
    { key: 'placeName', label: 'Name', color: 'text-gray-300', width: '150px' },
    { key: 'address', label: 'Address', color: 'text-gray-400', width: '200px' },
    { key: 'role', label: 'Role', color: 'text-yellow-400', format: 'badge', width: '80px' },
    { key: 'placeType', label: 'Type', color: 'text-blue-400', width: '80px' },
    { key: 'latitude', label: 'Lat', color: 'text-gray-500', width: '80px' },
    { key: 'longitude', label: 'Lng', color: 'text-gray-500', width: '80px' },
    { key: 'createdAt', label: 'Created', color: 'text-gray-500', format: 'date', width: '90px' }
];

/**
 * Get a Lucide icon component by name
 */
const getIconComponent = (iconName) => {
    if (!iconName) return HelpCircle;
    if (LucideIcons[iconName]) return LucideIcons[iconName];
    const capitalized = iconName.charAt(0).toUpperCase() + iconName.slice(1);
    if (LucideIcons[capitalized]) return LucideIcons[capitalized];
    const pascalCase = iconName.split(/[-_]/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('');
    if (LucideIcons[pascalCase]) return LucideIcons[pascalCase];
    return HelpCircle;
};

/**
 * DebugPanelTabs - Debug panel as bottom drawer with tabs
 * Shows data from DB, localStorage and FileServer
 * Visible only for admin users
 */
const DebugPanelTabs = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const [loading, setLoading] = useState(false);
    const [debugLogs, setDebugLogs] = useState([]);
    const [panelHeight, setPanelHeight] = useState(50); // Height in vh (50% by default)
    const [isDragging, setIsDragging] = useState(false);

    // Data states
    const [modules, setModules] = useState([]);
    const [modulesDebugData, setModulesDebugData] = useState({ db: [], local: [], activeLocal: [], activeDb: [], subscription: null, profileType: null });
    const [dbNotifications, setDbNotifications] = useState([]);
    const [fileServerNotifications, setFileServerNotifications] = useState([]);
    const [dbMeetings, setDbMeetings] = useState([]);
    const [contactsData, setContactsData] = useState({ db: [], local: [], server: [], loading: false });
    const [placesData, setPlacesData] = useState({ db: [], local: [], server: [], syncDebug: null, loading: false });
    const [selectedStorageKey, setSelectedStorageKey] = useState(null); // For localStorage key modal

    // Get user info
    const userId = parseInt(localStorage.getItem('userId'), 10);
    const userUuid = localStorage.getItem('userUuid');
    const authToken = localStorage.getItem('authToken');

    // Check if user is admin
    let isAdmin = false;
    let cabazaUser = null;
    try {
        cabazaUser = JSON.parse(localStorage.getItem('cabaza_user') || '{}');
        isAdmin = cabazaUser.isAdmin === true || cabazaUser.isAdmin === 'true' || cabazaUser.isAdmin === 1 || cabazaUser.isAdmin === '1';
    } catch (e) {
        console.error('Error parsing cabaza_user:', e);
    }

    // Get notifications from context
    const { notifications: contextNotifications, refresh: refreshNotifications } = useNotifications();

    // Get preferences context for refreshing active modules
    const { loadUserActiveModules } = usePreferences();

    // Add log entry
    const addLog = (message, type = 'info') => {
        const time = new Date().toLocaleTimeString();
        setDebugLogs(prev => [...prev.slice(-50), { time, message, type }]);
        console.log(`[Debug ${time}] ${message}`);
    };

    // Get user info helper
    const getUserInfo = () => {
        try {
            const userJson = localStorage.getItem('cabaza_user');
            if (!userJson) return { userId, peopleUuid: null, storageKey: null };
            const user = JSON.parse(userJson);
            return {
                userId,
                peopleUuid: user.peopleUuid || user.peopleuuid || userUuid,
                storageKey: user.storageKey
            };
        } catch (e) {
            return { userId, peopleUuid: userUuid, storageKey: null };
        }
    };

    // Fix User Info - fetch from API if missing
    const fixUserInfo = async () => {
        if (!userUuid) {
            addLog('ERROR: userUuid not found in localStorage', 'error');
            return;
        }
        if (!authToken) {
            addLog('ERROR: authToken not found in localStorage', 'error');
            return;
        }
        addLog(`Fetching user info for UUID: ${userUuid.substring(0, 12)}...`);
        try {
            const response = await fetch(`${API_BASE_URL}/peoples/${userUuid}`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });
            addLog(`Response status: ${response.status}`);

            const text = await response.text();
            if (!text || text.length === 0) {
                addLog('ERROR: Empty response from API', 'error');
                return;
            }

            let result;
            try {
                result = JSON.parse(text);
            } catch (parseErr) {
                addLog(`ERROR: Invalid JSON`, 'error');
                return;
            }

            if (result.success || result.SUCCESS) {
                const data = result.data || result.DATA;
                const userObj = {
                    id: userId,
                    email: data.email || localStorage.getItem('userEmail'),
                    firstName: data.firstname || data.firstName,
                    lastName: data.lastname || data.lastName,
                    peopleUuid: data.peopleuuid || data.peopleUuid || userUuid,
                    storageKey: data.storagekey || data.storageKey,
                    isAdmin: data.isadmin || data.isAdmin
                };
                localStorage.setItem('cabaza_user', JSON.stringify(userObj));
                addLog(`SUCCESS: cabaza_user updated, storageKey: ${userObj.storageKey ? 'present' : 'MISSING'}`, userObj.storageKey ? 'success' : 'error');
                refreshAll();
            } else {
                addLog(`ERROR: ${result.message || result.MESSAGE}`, 'error');
            }
        } catch (err) {
            addLog(`ERROR: ${err.message}`, 'error');
        }
    };

    // Fetch modules from API
    const fetchModules = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/modules?lang=fr&isActive=1`);
            const data = await response.json();
            const items = Array.isArray(data) ? data : (data.data ?? data.DATA ?? []);
            if (items.length > 0) {
                setModules(items);
                addLog(`Modules: ${items.length}`, 'success');
            }
        } catch (err) {
            addLog(`Modules error: ${err.message}`, 'error');
        }
    };

    // Fetch modules debug data (DB vs localStorage + user active modules)
    const fetchModulesDebugData = async () => {
        try {
            // Get from API (all modules, all fields)
            const response = await fetch(`${API_BASE_URL}/modules?isActive=1`);
            const dbData = await response.json();
            const dbItems = Array.isArray(dbData) ? dbData : (dbData.data ?? dbData.DATA ?? []);

            // Get from localStorage (all modules)
            const localStorageData = localStorage.getItem('cabazaModulesList');
            const localItems = localStorageData ? JSON.parse(localStorageData) : [];

            // Get user's active modules from localStorage
            const activeLocalModules = getUserActiveModules();

            // Get user's profile type
            const profileTypeId = localStorage.getItem('userProfileTypeId');
            const profileTypeCode = localStorage.getItem('profileTypeCode');

            // Get user's subscription
            let subscription = null;
            let activeDbModules = [];
            if (userUuid) {
                try {
                    subscription = await getSubscription(userUuid);
                } catch (e) {
                    subscription = { subscriptionType: 'FREE', error: e.message };
                }

                // Get modules from DB based on profile type and subscription
                if (profileTypeId && subscription) {
                    const subType = subscription?.subscriptionType || subscription?.tarifCode || 'FREE';
                    try {
                        activeDbModules = await getModulesByProfileTypeAndSubscription(
                            parseInt(profileTypeId, 10),
                            subType
                        );
                    } catch (e) {
                        addLog(`Error fetching active modules from DB: ${e.message}`, 'error');
                    }
                }
            }

            setModulesDebugData({
                db: dbItems,
                local: localItems,
                activeLocal: activeLocalModules,
                activeDb: activeDbModules,
                subscription,
                profileType: { id: profileTypeId, code: profileTypeCode }
            });
            addLog(`Modules Debug - DB: ${dbItems.length}, Local: ${localItems.length}, Active(local): ${activeLocalModules.length}, Active(db): ${activeDbModules.length}`, 'success');
        } catch (err) {
            addLog(`Modules debug error: ${err.message}`, 'error');
        }
    };

    // Fetch notifications debug
    const fetchDebugNotifications = async () => {
        if (!userId) return;
        try {
            const response = await fetch(`${API_BASE_URL}/notifications/debug?peopleId=${userId}`);
            const data = await response.json();
            if (data.success ?? data.SUCCESS) {
                setDbNotifications(data.db ?? data.DB ?? []);
                setFileServerNotifications(data.fileServer ?? data.FILESERVER ?? []);
                addLog(`Notifications - DB: ${(data.db ?? data.DB ?? []).length}, FS: ${(data.fileServer ?? data.FILESERVER ?? []).length}`, 'success');
            }
        } catch (err) {
            addLog(`Notifications error: ${err.message}`, 'error');
        }
    };

    // Fetch meetings
    const fetchDbMeetings = async () => {
        if (!userId) return;
        try {
            const response = await fetch(`${API_BASE_URL}/meetings?peopleId=${userId}`);
            const data = await response.json();
            const items = data.data ?? data.DATA ?? [];
            setDbMeetings(items);
            addLog(`Meetings: ${items.length}`, 'success');
        } catch (err) {
            addLog(`Meetings error: ${err.message}`, 'error');
        }
    };

    // Fetch contacts from DB
    const fetchDbContacts = async () => {
        if (!userId) return;
        try {
            const response = await fetch(`${API_BASE_URL}/contacts?ownerId=${userId}`);
            const data = await response.json();
            const items = data.data ?? data.DATA ?? (Array.isArray(data) ? data : []);
            setContactsData(prev => ({ ...prev, db: items }));
            addLog(`Contacts DB: ${items.length}`, 'success');
        } catch (err) {
            addLog(`Contacts DB error: ${err.message}`, 'error');
            setContactsData(prev => ({ ...prev, db: [] }));
        }
    };

    // Fetch places from DB
    const fetchDbPlaces = async () => {
        if (!userId) return;
        try {
            const response = await fetch(`${API_BASE_URL}/places/people/${userId}`);
            const data = await response.json();
            const items = data.data ?? data.DATA ?? (Array.isArray(data) ? data : []);
            setPlacesData(prev => ({ ...prev, db: items }));
            addLog(`Places DB: ${items.length}`, 'success');
        } catch (err) {
            addLog(`Places DB error: ${err.message}`, 'error');
            setPlacesData(prev => ({ ...prev, db: [] }));
        }
    };

    // Load contacts data (DB + localStorage + Server File)
    const loadContactsData = async () => {
        try {
            // Load from DB
            await fetchDbContacts();

            // Load from localStorage
            const localData = localStorage.getItem('cabaza_user_contacts');
            let localContacts = [];
            if (localData) {
                const parsed = JSON.parse(localData);
                const contacts = parsed[userId] || [];
                localContacts = Array.isArray(contacts) ? contacts : [];
            }
            setContactsData(prev => ({ ...prev, local: localContacts }));
            addLog(`Contacts localStorage: ${localContacts.length}`, 'success');

            // Load from Server File
            const userInfo = getUserInfo();
            if (userInfo?.peopleUuid && userInfo?.storageKey) {
                try {
                    const serverData = await loadContactsFromFile(userInfo.peopleUuid, userInfo.storageKey);
                    setContactsData(prev => ({ ...prev, server: serverData || [] }));
                    addLog(`Contacts Server File: ${serverData?.length || 0}`, 'success');
                } catch (err) {
                    addLog(`Contacts Server File error: ${err.message}`, 'error');
                    setContactsData(prev => ({ ...prev, server: [] }));
                }
            }
        } catch (err) {
            addLog(`Contacts localStorage error: ${err.message}`, 'error');
        }
    };

    // Contacts - Test Load from server
    const contactsTestLoad = async () => {
        const userInfo = getUserInfo();
        if (!userInfo?.peopleUuid || !userInfo?.storageKey) {
            addLog('ERROR: peopleUuid or storageKey missing', 'error');
            return;
        }
        setContactsData(prev => ({ ...prev, loading: true }));
        addLog('Contacts: Loading from server file...');
        try {
            const serverData = await loadContactsFromFile(userInfo.peopleUuid, userInfo.storageKey);
            if (serverData && serverData.length > 0) {
                addLog(`SUCCESS: Loaded ${serverData.length} contacts from server`, 'success');
                // Update localStorage
                const data = localStorage.getItem('cabaza_user_contacts');
                const allData = data ? JSON.parse(data) : {};
                allData[userId] = serverData;
                localStorage.setItem('cabaza_user_contacts', JSON.stringify(allData));
                setContactsData(prev => ({ ...prev, local: serverData, server: serverData }));
            } else {
                addLog('Server file empty or not found', 'error');
            }
        } catch (err) {
            addLog(`ERROR: ${err.message}`, 'error');
        } finally {
            setContactsData(prev => ({ ...prev, loading: false }));
        }
    };

    // Contacts - Test Save to server
    const contactsTestSave = async () => {
        const userInfo = getUserInfo();
        if (!userInfo?.peopleUuid || !userInfo?.storageKey) {
            addLog('ERROR: peopleUuid or storageKey missing', 'error');
            return;
        }
        setContactsData(prev => ({ ...prev, loading: true }));
        addLog('Contacts: Saving to server file...');
        try {
            const result = await saveContactsToFile(userInfo.peopleUuid, userInfo.storageKey, contactsData.local);
            const success = result.success || result.SUCCESS;
            if (success) {
                addLog(`SUCCESS: Saved ${contactsData.local.length} contacts to server`, 'success');
                await loadContactsData();
            } else {
                addLog(`ERROR: ${result.message || result.MESSAGE}`, 'error');
            }
        } catch (err) {
            addLog(`ERROR: ${err.message}`, 'error');
        } finally {
            setContactsData(prev => ({ ...prev, loading: false }));
        }
    };

    // Contacts - Full Resync
    const contactsFullResync = async () => {
        const userInfo = getUserInfo();
        setContactsData(prev => ({ ...prev, loading: true }));
        addLog('Contacts: Full resync...');
        localStorage.removeItem('cabaza_user_contacts');
        setContactsData(prev => ({ ...prev, local: [], server: [] }));

        try {
            if (userInfo?.peopleUuid && userInfo?.storageKey) {
                const serverData = await loadContactsFromFile(userInfo.peopleUuid, userInfo.storageKey);
                if (serverData && serverData.length > 0) {
                    const allData = {};
                    allData[userId] = serverData;
                    localStorage.setItem('cabaza_user_contacts', JSON.stringify(allData));
                    setContactsData(prev => ({ ...prev, local: serverData, server: serverData }));
                    addLog(`SUCCESS: Resynced ${serverData.length} contacts`, 'success');
                } else {
                    addLog('No server file found', 'error');
                }
            }
        } catch (err) {
            addLog(`ERROR: ${err.message}`, 'error');
        } finally {
            setContactsData(prev => ({ ...prev, loading: false }));
        }
    };

    // Load places data (DB + localStorage + Server File)
    const loadPlacesData = async () => {
        try {
            // Load from DB
            await fetchDbPlaces();

            // Load from localStorage
            const localData = localStorage.getItem('cabaza_user_places');
            let localPlaces = [];
            if (localData) {
                const parsed = JSON.parse(localData);
                if (Array.isArray(parsed)) {
                    localPlaces = parsed;
                } else if (parsed[userId]) {
                    const userPlaces = parsed[userId];
                    localPlaces = Array.isArray(userPlaces) ? userPlaces : (userPlaces?.places || []);
                }
            }
            setPlacesData(prev => ({ ...prev, local: localPlaces }));
            addLog(`Places localStorage: ${localPlaces.length}`, 'success');

            // Get sync debug if available
            import('../../services/userPlacesService').then(module => {
                if (module.getLastSyncDebug) {
                    const syncDebug = module.getLastSyncDebug();
                    if (syncDebug) {
                        setPlacesData(prev => ({ ...prev, syncDebug }));
                    }
                }
            }).catch(() => {});

            // Load from Server File
            const userInfo = getUserInfo();
            if (userInfo?.peopleUuid && userInfo?.storageKey) {
                try {
                    const serverData = await loadPlacesFromFile(userInfo.peopleUuid, userInfo.storageKey);
                    setPlacesData(prev => ({ ...prev, server: serverData || [] }));
                    addLog(`Places Server File: ${serverData?.length || 0}`, 'success');
                } catch (err) {
                    addLog(`Places Server File error: ${err.message}`, 'error');
                    setPlacesData(prev => ({ ...prev, server: [] }));
                }
            }
        } catch (err) {
            addLog(`Places localStorage error: ${err.message}`, 'error');
        }
    };

    // Places - Test Load from server
    const placesTestLoad = async () => {
        const userInfo = getUserInfo();
        if (!userInfo?.peopleUuid || !userInfo?.storageKey) {
            addLog('ERROR: peopleUuid or storageKey missing', 'error');
            return;
        }
        setPlacesData(prev => ({ ...prev, loading: true }));
        addLog('Places: Loading from server file...');
        try {
            const serverData = await loadPlacesFromFile(userInfo.peopleUuid, userInfo.storageKey);
            if (serverData && serverData.length > 0) {
                addLog(`SUCCESS: Loaded ${serverData.length} places from server`, 'success');
                // Update localStorage
                const data = localStorage.getItem('cabaza_user_places');
                const allData = data ? JSON.parse(data) : {};
                allData[userId] = serverData;
                localStorage.setItem('cabaza_user_places', JSON.stringify(allData));
                setPlacesData(prev => ({ ...prev, local: serverData, server: serverData }));
            } else {
                addLog('Server file empty or not found', 'error');
            }
        } catch (err) {
            addLog(`ERROR: ${err.message}`, 'error');
        } finally {
            setPlacesData(prev => ({ ...prev, loading: false }));
        }
    };

    // Places - Test Save to server
    const placesTestSave = async () => {
        const userInfo = getUserInfo();
        if (!userInfo?.peopleUuid || !userInfo?.storageKey) {
            addLog('ERROR: peopleUuid or storageKey missing', 'error');
            return;
        }
        setPlacesData(prev => ({ ...prev, loading: true }));
        addLog('Places: Saving to server file...');
        try {
            const result = await savePlacesToFile(userInfo.peopleUuid, userInfo.storageKey, placesData.local);
            const success = result.success || result.SUCCESS;
            if (success) {
                addLog(`SUCCESS: Saved ${placesData.local.length} places to server`, 'success');
                await loadPlacesData();
            } else {
                addLog(`ERROR: ${result.message || result.MESSAGE}`, 'error');
            }
        } catch (err) {
            addLog(`ERROR: ${err.message}`, 'error');
        } finally {
            setPlacesData(prev => ({ ...prev, loading: false }));
        }
    };

    // Places - Full Resync
    const placesFullResync = async () => {
        const userInfo = getUserInfo();
        setPlacesData(prev => ({ ...prev, loading: true }));
        addLog('Places: Full resync...');
        localStorage.removeItem('cabaza_user_places');
        setPlacesData(prev => ({ ...prev, local: [], server: [] }));

        try {
            if (userInfo?.peopleUuid && userInfo?.storageKey) {
                const serverData = await loadPlacesFromFile(userInfo.peopleUuid, userInfo.storageKey);
                if (serverData && serverData.length > 0) {
                    const allData = {};
                    allData[userId] = serverData;
                    localStorage.setItem('cabaza_user_places', JSON.stringify(allData));
                    setPlacesData(prev => ({ ...prev, local: serverData, server: serverData }));
                    addLog(`SUCCESS: Resynced ${serverData.length} places`, 'success');
                } else {
                    addLog('No server file found', 'error');
                }
            }
        } catch (err) {
            addLog(`ERROR: ${err.message}`, 'error');
        } finally {
            setPlacesData(prev => ({ ...prev, loading: false }));
        }
    };

    // Refresh all data
    const refreshAll = async () => {
        setLoading(true);
        addLog('Refreshing...');
        try {
            await fetchModules();
            await Promise.all([
                fetchDebugNotifications(),
                fetchDbMeetings(),
                fetchModulesDebugData(),
                refreshNotifications?.()
            ]);
            loadContactsData();
            loadPlacesData();
            addLog('Done', 'success');
        } catch (err) {
            addLog(`Error: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Toggle panel
    const handleToggle = () => {
        const newState = !isOpen;
        setIsOpen(newState);
        if (newState) {
            refreshAll();
        }
    };

    // Fixed tabs (General + Modules + Notifications which is not a DB module)
    const fixedTabs = [
        { id: 'general', label: 'Général', icon: User, color: 'text-gray-400' },
        { id: 'modules', label: 'Modules', icon: LayoutGrid, color: 'text-purple-400' },
        { id: 'notifications', label: 'Notifications', icon: Bell, color: 'text-yellow-400' }
    ];

    // All module tabs from API (including contacts, meetings, places, notifications)
    const moduleTabs = useMemo(() => {
        const colors = [
            'text-blue-400', 'text-green-400', 'text-purple-400', 'text-rose-400',
            'text-cyan-400', 'text-yellow-400', 'text-pink-400', 'text-indigo-400', 'text-orange-400', 'text-teal-400'
        ];

        return modules.map((mod, index) => {
            const code = mod.code || mod.CODE;
            const name = mod.name_fr || mod.NAME_FR || mod.name || mod.NAME || code;
            const icon = mod.icon || mod.ICON;
            const color = mod.color || mod.COLOR;
            return {
                id: code?.toLowerCase(),
                code: code?.toUpperCase(),
                label: name,
                icon: getIconComponent(icon),
                color: color ? `text-[${color}]` : colors[index % colors.length]
            };
        });
    }, [modules]);

    const allTabs = [...fixedTabs, ...moduleTabs];

    // Format date
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleString('fr-BE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
        } catch { return dateStr; }
    };

    // Format cell value based on field config
    const formatCellValue = (item, field) => {
        const value = item[field.key];

        if (field.format === 'boolean') {
            if (value === true || value === 'true' || value === 1) {
                return <span className="text-green-400">{field.trueIcon || '✓'}</span>;
            }
            return <span className="text-gray-600">-</span>;
        }

        if (field.format === 'date' && value) {
            try {
                return new Date(value).toLocaleDateString('fr-BE');
            } catch { return value; }
        }

        if (field.format === 'truncate' && value) {
            const str = String(value);
            return str.length > 8 ? str.substring(0, 8) + '...' : str;
        }

        if (field.format === 'badge' && value) {
            const bgColor = value === 'PUBLIC' ? 'bg-green-900 text-green-300' :
                           value === 'PRIVATE' ? 'bg-purple-900 text-purple-300' :
                           'bg-gray-700 text-gray-300';
            return <span className={`px-1.5 py-0.5 rounded text-xs ${bgColor}`}>{value}</span>;
        }

        return value || '-';
    };

    // Handle drag resize
    const handleMouseDown = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e) => {
            const windowHeight = window.innerHeight;
            const newHeight = ((windowHeight - e.clientY) / windowHeight) * 100;
            // Clamp between 20% and 90%
            setPanelHeight(Math.min(90, Math.max(20, newHeight)));
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    // Handle touch resize for mobile
    const handleTouchStart = (e) => {
        setIsDragging(true);
    };

    useEffect(() => {
        if (!isDragging) return;

        const handleTouchMove = (e) => {
            const touch = e.touches[0];
            const windowHeight = window.innerHeight;
            const newHeight = ((windowHeight - touch.clientY) / windowHeight) * 100;
            setPanelHeight(Math.min(90, Math.max(20, newHeight)));
        };

        const handleTouchEnd = () => {
            setIsDragging(false);
        };

        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);

        return () => {
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isDragging]);

    // Render General tab - localStorage user info
    const renderGeneralTab = () => {
        const allLocalStorageKeys = Object.keys(localStorage).filter(k => k.startsWith('cabaza') || ['userId', 'userUuid', 'authToken', 'userEmail', 'userName', 'userRole'].includes(k));
        const userInfo = getUserInfo();

        return (
            <div className="space-y-4">
                {/* User Info */}
                <div className="bg-gray-800 rounded p-3">
                    <div className="flex justify-between items-center mb-2">
                        <h4 className="text-sm font-mono text-white flex items-center gap-2">
                            <User className="w-4 h-4" />
                            User Info
                        </h4>
                        <button
                            onClick={fixUserInfo}
                            disabled={loading}
                            className="text-xs bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-500 disabled:opacity-50 flex items-center gap-1"
                        >
                            <Wrench className="w-3 h-3" />
                            Fix User Info
                        </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs font-mono">
                        <div><span className="text-gray-400">userId:</span> <span className="text-cyan-400">{userId || '-'}</span></div>
                        <div><span className="text-gray-400">userUuid:</span> <span className="text-purple-400">{userUuid?.substring(0, 12) || '-'}...</span></div>
                        <div><span className="text-gray-400">email:</span> <span className="text-green-400">{cabazaUser?.email || localStorage.getItem('userEmail') || '-'}</span></div>
                        <div><span className="text-gray-400">isAdmin:</span> <span className={isAdmin ? 'text-green-400' : 'text-red-400'}>{isAdmin ? 'Yes' : 'No'}</span></div>
                        <div><span className="text-gray-400">storageKey:</span> <span className={userInfo?.storageKey ? 'text-green-400' : 'text-red-400'}>{userInfo?.storageKey ? userInfo.storageKey.substring(0, 16) + '...' : 'MISSING'}</span></div>
                        <div><span className="text-gray-400">peopleUuid:</span> <span className={userInfo?.peopleUuid ? 'text-green-400' : 'text-red-400'}>{userInfo?.peopleUuid ? userInfo.peopleUuid.substring(0, 12) + '...' : 'MISSING'}</span></div>
                        <div><span className="text-gray-400">firstName:</span> <span className="text-gray-300">{cabazaUser?.firstName || '-'}</span></div>
                        <div><span className="text-gray-400">lastName:</span> <span className="text-gray-300">{cabazaUser?.lastName || '-'}</span></div>
                    </div>
                </div>

                {/* LocalStorage Keys */}
                <div className="bg-gray-800 rounded p-3">
                    <h4 className="text-sm font-mono text-white mb-2 flex items-center gap-2">
                        <HardDrive className="w-4 h-4" />
                        LocalStorage Keys ({allLocalStorageKeys.length})
                        <span className="text-gray-500 text-[10px]">(cliquer sur une clé pour voir le contenu)</span>
                    </h4>
                    <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-xs font-mono">
                            <thead className="text-gray-400 border-b border-gray-700 sticky top-0 bg-gray-800">
                                <tr>
                                    <th className="text-left p-1">Key</th>
                                    <th className="text-left p-1">Size</th>
                                    <th className="text-left p-1">Preview</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allLocalStorageKeys.sort().map(key => {
                                    const value = localStorage.getItem(key) || '';
                                    const size = new Blob([value]).size;
                                    let preview = value.substring(0, 50);
                                    if (value.length > 50) preview += '...';
                                    return (
                                        <tr
                                            key={key}
                                            className="border-b border-gray-700 hover:bg-gray-700 cursor-pointer"
                                            onClick={() => setSelectedStorageKey(key)}
                                        >
                                            <td className="p-1 text-cyan-400 hover:text-cyan-300 hover:underline">{key}</td>
                                            <td className="p-1 text-gray-500">{size > 1024 ? `${(size/1024).toFixed(1)}KB` : `${size}B`}</td>
                                            <td className="p-1 text-gray-400 max-w-xs truncate" title={value}>{preview}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* cabaza_user JSON */}
                <details className="bg-gray-800 rounded">
                    <summary className="p-2 cursor-pointer text-gray-400 text-xs hover:text-white">cabaza_user JSON</summary>
                    <pre className="p-2 text-xs text-gray-300 overflow-x-auto max-h-40">{JSON.stringify(cabazaUser, null, 2)}</pre>
                </details>

                {/* Modal for localStorage key content - rendered in portal to be above sidebar */}
                {selectedStorageKey && createPortal(
                    <div
                        className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10000]"
                        onClick={() => setSelectedStorageKey(null)}
                    >
                        <div
                            className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl w-[90vw] max-w-4xl max-h-[80vh] flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="flex justify-between items-center p-4 border-b border-gray-700">
                                <h3 className="text-sm font-mono text-cyan-400 flex items-center gap-2">
                                    <HardDrive className="w-4 h-4" />
                                    {selectedStorageKey}
                                </h3>
                                <button
                                    onClick={() => setSelectedStorageKey(null)}
                                    className="text-gray-400 hover:text-white p-1"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            {/* Modal Content */}
                            <div className="flex-1 overflow-auto p-4">
                                <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap break-words">
                                    {(() => {
                                        const value = localStorage.getItem(selectedStorageKey) || '';
                                        try {
                                            return JSON.stringify(JSON.parse(value), null, 2);
                                        } catch {
                                            return value;
                                        }
                                    })()}
                                </pre>
                            </div>
                            {/* Modal Footer */}
                            <div className="flex justify-end gap-2 p-4 border-t border-gray-700">
                                <button
                                    onClick={() => {
                                        const value = localStorage.getItem(selectedStorageKey) || '';
                                        navigator.clipboard.writeText(value);
                                        addLog(`Copied ${selectedStorageKey} to clipboard`, 'success');
                                    }}
                                    className="text-xs bg-blue-700 text-white px-3 py-1.5 rounded hover:bg-blue-600 flex items-center gap-1"
                                >
                                    Copier
                                </button>
                                <button
                                    onClick={() => setSelectedStorageKey(null)}
                                    className="text-xs bg-gray-700 text-white px-3 py-1.5 rounded hover:bg-gray-600"
                                >
                                    Fermer
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        );
    };

    // Sync active modules from DB to localStorage
    const syncActiveModules = async () => {
        addLog('Syncing active modules from DB to localStorage...');
        try {
            const result = await loadUserActiveModules();
            addLog(`SUCCESS: Synced ${result?.length || 0} active modules`, 'success');
            // Refresh debug data to show updated counts
            await fetchModulesDebugData();
        } catch (err) {
            addLog(`ERROR: ${err.message}`, 'error');
        }
    };

    // Render Modules tab (DB vs localStorage + user subscription/active modules)
    const renderModulesTab = () => {
        const { db: dbModules, local: localModules, activeLocal, activeDb, subscription, profileType } = modulesDebugData;

        // All fields to display for full module list
        const moduleFields = [
            'id', 'code', 'icon', 'color', 'sortOrder', 'isActive',
            'name_fr', 'name_en', 'name_nl', 'name_de', 'name_es', 'name_it', 'name_pt', 'name_el',
            'description_fr', 'description_en', 'description_nl', 'description_de', 'description_es', 'description_it', 'description_pt', 'description_el'
        ];

        // Simpler fields for active modules
        const activeModuleFields = ['id', 'code', 'name_fr', 'subscriptiontype'];

        const renderModuleTable = (data, title, icon, color, fields = moduleFields) => (
            <div className="bg-gray-800 rounded p-3">
                <h4 className={`text-sm font-mono ${color} mb-2 flex items-center gap-2`}>
                    {icon}
                    {title} ({data.length})
                </h4>
                <div className="overflow-x-auto overflow-y-auto max-h-64">
                    <table className="w-full text-xs font-mono">
                        <thead className="text-gray-400 border-b border-gray-700 sticky top-0 bg-gray-800">
                            <tr>
                                {fields.map(field => (
                                    <th key={field} className="text-left p-1 whitespace-nowrap">{field}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((mod, i) => (
                                <tr key={mod.id || mod.ID || i} className="border-b border-gray-700 hover:bg-gray-700">
                                    {fields.map(field => {
                                        const value = mod[field] ?? mod[field.toUpperCase()] ?? mod[field.toLowerCase()] ?? '';
                                        let colorClass = 'text-gray-300';
                                        if (field === 'id') colorClass = 'text-purple-400';
                                        else if (field === 'code') colorClass = 'text-cyan-400';
                                        else if (field === 'icon') colorClass = 'text-yellow-400';
                                        else if (field === 'color') colorClass = 'text-pink-400';
                                        else if (field === 'subscriptiontype') colorClass = 'text-orange-400';
                                        else if (field.startsWith('name_')) colorClass = 'text-green-400';
                                        else if (field.startsWith('description_')) colorClass = 'text-blue-400';

                                        return (
                                            <td key={field} className={`p-1 ${colorClass} truncate max-w-[120px]`} title={String(value)}>
                                                {field === 'color' && value ? (
                                                    <span className="flex items-center gap-1">
                                                        <span className="w-3 h-3 rounded" style={{ backgroundColor: value }} />
                                                        {value}
                                                    </span>
                                                ) : field === 'icon' && value ? (
                                                    <span className="flex items-center gap-1">
                                                        {(() => { const Icon = getIconComponent(value); return <Icon className="w-3 h-3" />; })()}
                                                        {value}
                                                    </span>
                                                ) : (
                                                    String(value).substring(0, 30)
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {data.length === 0 && <p className="text-yellow-400 p-2 text-center text-xs">Empty</p>}
                </div>
            </div>
        );

        return (
            <div className="space-y-4">
                {/* User Profile & Subscription Info */}
                <div className="bg-gray-800 rounded p-3">
                    <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-mono text-white flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Profil & Abonnement
                        </h4>
                        <button
                            onClick={syncActiveModules}
                            disabled={loading}
                            className="text-xs bg-green-700 text-white px-2 py-1 rounded hover:bg-green-600 disabled:opacity-50 flex items-center gap-1"
                        >
                            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                            Sync Modules
                        </button>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs font-mono">
                        {/* Profile Type */}
                        <div className="bg-gray-700 rounded p-2">
                            <div className="text-gray-400 mb-1 flex items-center gap-1">
                                <Shield className="w-3 h-3" />
                                Profile Type
                            </div>
                            <div className="text-cyan-400">
                                ID: {profileType?.id || 'N/A'}
                            </div>
                            <div className="text-blue-400">
                                Code: {profileType?.code || 'N/A'}
                            </div>
                        </div>

                        {/* Subscription */}
                        <div className="bg-gray-700 rounded p-2">
                            <div className="text-gray-400 mb-1 flex items-center gap-1">
                                <Crown className="w-3 h-3" />
                                Abonnement
                            </div>
                            <div className="text-yellow-400">
                                {subscription?.subscriptionType || subscription?.tarifCode || 'FREE'}
                            </div>
                            <div className={subscription?.isActive ? 'text-green-400' : 'text-red-400'}>
                                {subscription?.isActive ? 'Actif' : 'Inactif'}
                            </div>
                            {subscription?.error && (
                                <div className="text-red-400 text-[10px]">{subscription.error}</div>
                            )}
                        </div>

                        {/* Active Modules Count */}
                        <div className="bg-gray-700 rounded p-2">
                            <div className="text-gray-400 mb-1 flex items-center gap-1">
                                <LayoutGrid className="w-3 h-3" />
                                Modules Actifs
                            </div>
                            <div className="text-green-400">
                                LocalStorage: {activeLocal?.length || 0}
                            </div>
                            <div className="text-cyan-400">
                                DB (selon abo): {activeDb?.length || 0}
                            </div>
                            <div className={activeLocal?.length === activeDb?.length ? 'text-green-400' : 'text-red-400'}>
                                {activeLocal?.length === activeDb?.length ? '✓ Sync OK' : '⚠️ MISMATCH'}
                            </div>
                        </div>

                        {/* All Modules Count */}
                        <div className="bg-gray-700 rounded p-2">
                            <div className="text-gray-400 mb-1 flex items-center gap-1">
                                <Database className="w-3 h-3" />
                                Tous les Modules
                            </div>
                            <div className="text-cyan-400">
                                DB: {dbModules?.length || 0}
                            </div>
                            <div className="text-orange-400">
                                LocalStorage: {localModules?.length || 0}
                            </div>
                            <div className={dbModules?.length === localModules?.length ? 'text-green-400' : 'text-red-400'}>
                                {dbModules?.length === localModules?.length ? '✓ Sync OK' : '⚠️ MISMATCH'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Active Modules Comparison */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Active Modules from LocalStorage */}
                    {renderModuleTable(
                        activeLocal || [],
                        'Modules Actifs (LocalStorage)',
                        <HardDrive className="w-4 h-4" />,
                        'text-orange-400',
                        activeModuleFields
                    )}

                    {/* Active Modules from DB (based on subscription) */}
                    {renderModuleTable(
                        activeDb || [],
                        'Modules Actifs (DB selon abonnement)',
                        <Database className="w-4 h-4" />,
                        'text-cyan-400',
                        activeModuleFields
                    )}
                </div>

                {/* All Modules - Collapsible */}
                <details className="bg-gray-800 rounded">
                    <summary className="cursor-pointer p-3 text-sm font-mono text-white flex items-center gap-2">
                        <Database className="w-4 h-4" />
                        Tous les Modules (DB & LocalStorage) - Cliquer pour déplier
                    </summary>
                    <div className="p-3 pt-0 space-y-4">
                        {/* DB Modules */}
                        {renderModuleTable(dbModules || [], 'Database (tous)', <Database className="w-4 h-4" />, 'text-cyan-400')}

                        {/* LocalStorage Modules */}
                        {renderModuleTable(localModules || [], 'LocalStorage (tous)', <HardDrive className="w-4 h-4" />, 'text-orange-400')}
                    </div>
                </details>

                {/* User Storage (new format) - Collapsible */}
                <details className="bg-gray-800 rounded">
                    <summary className="cursor-pointer p-3 text-sm font-mono text-white flex items-center gap-2">
                        <Archive className="w-4 h-4" />
                        Preferences Cache (nouveau format simplifié) - Cliquer pour déplier
                    </summary>
                    <div className="p-3 pt-0 space-y-4">
                        {(() => {
                            const currentUserId = localStorage.getItem('userId');
                            const prefsCache = JSON.parse(localStorage.getItem('cabazaPreferencesCache') || '{}');
                            const activeModules = JSON.parse(localStorage.getItem('cabazaUserActiveModules') || '[]');

                            return (
                                <>
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs font-mono">
                                        <div className="bg-gray-700 rounded p-2">
                                            <div className="text-gray-400 mb-1">Current User ID</div>
                                            <div className="text-cyan-400">{currentUserId || 'N/A'}</div>
                                        </div>
                                        <div className="bg-gray-700 rounded p-2">
                                            <div className="text-gray-400 mb-1">Pinned Items</div>
                                            <div className="text-green-400">{prefsCache?.pinnedItems?.length || 0}</div>
                                        </div>
                                        <div className="bg-gray-700 rounded p-2">
                                            <div className="text-gray-400 mb-1">Active Modules</div>
                                            <div className="text-orange-400">{activeModules?.length || 0}</div>
                                        </div>
                                        <div className="bg-gray-700 rounded p-2">
                                            <div className="text-gray-400 mb-1">Module Groups</div>
                                            <div className="text-blue-400">{prefsCache?.moduleGroups?.length || 0}</div>
                                        </div>
                                    </div>
                                    {Object.keys(prefsCache).length > 0 && (
                                        <div className="bg-gray-700 rounded p-2">
                                            <div className="text-gray-400 mb-2">Preferences Cache Keys</div>
                                            <div className="flex flex-wrap gap-2">
                                                {Object.keys(prefsCache).map(key => (
                                                    <span key={key} className="px-2 py-1 bg-gray-600 rounded text-xs">
                                                        <span className="text-cyan-400">{key}</span>
                                                        <span className="text-gray-400 ml-1">
                                                            ({Array.isArray(prefsCache[key]) ? prefsCache[key].length : typeof prefsCache[key]})
                                                        </span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </details>
            </div>
        );
    };

    // Render Notifications tab
    const renderNotificationsTab = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* DB */}
                <div className="bg-gray-800 rounded p-3">
                    <h4 className="text-sm font-mono text-cyan-400 mb-2 flex items-center gap-2">
                        <Database className="w-4 h-4" />
                        Database ({dbNotifications.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto">
                        <table className="w-full text-xs font-mono">
                            <thead className="text-gray-400 border-b border-gray-700 sticky top-0 bg-gray-800">
                                <tr><th className="text-left p-1">ID</th><th className="text-left p-1">Type</th><th className="text-left p-1">Title</th></tr>
                            </thead>
                            <tbody>
                                {dbNotifications.slice(0, 10).map((n, i) => (
                                    <tr key={n.id || i} className="border-b border-gray-700"><td className="p-1 text-purple-400">{n.id}</td><td className="p-1 text-yellow-400 truncate max-w-[80px]">{n.notificationType}</td><td className="p-1 text-gray-300 truncate max-w-[100px]">{n.title}</td></tr>
                                ))}
                            </tbody>
                        </table>
                        {dbNotifications.length === 0 && <p className="text-yellow-400 p-2 text-center text-xs">Empty</p>}
                    </div>
                </div>
                {/* FileServer */}
                <div className="bg-gray-800 rounded p-3">
                    <h4 className="text-sm font-mono text-green-400 mb-2 flex items-center gap-2">
                        <Cloud className="w-4 h-4" />
                        FileServer ({fileServerNotifications.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto">
                        <table className="w-full text-xs font-mono">
                            <thead className="text-gray-400 border-b border-gray-700 sticky top-0 bg-gray-800">
                                <tr><th className="text-left p-1">ID</th><th className="text-left p-1">Type</th><th className="text-left p-1">Title</th></tr>
                            </thead>
                            <tbody>
                                {fileServerNotifications.slice(0, 10).map((n, i) => (
                                    <tr key={n.id || i} className="border-b border-gray-700"><td className="p-1 text-purple-400">{n.id}</td><td className="p-1 text-yellow-400 truncate max-w-[80px]">{n.notificationType}</td><td className="p-1 text-gray-300 truncate max-w-[100px]">{n.title}</td></tr>
                                ))}
                            </tbody>
                        </table>
                        {fileServerNotifications.length === 0 && <p className="text-yellow-400 p-2 text-center text-xs">Empty</p>}
                    </div>
                </div>
                {/* LocalStorage */}
                <div className="bg-gray-800 rounded p-3">
                    <h4 className="text-sm font-mono text-orange-400 mb-2 flex items-center gap-2">
                        <HardDrive className="w-4 h-4" />
                        LocalStorage ({contextNotifications?.length || 0})
                    </h4>
                    <div className="max-h-40 overflow-y-auto">
                        <table className="w-full text-xs font-mono">
                            <thead className="text-gray-400 border-b border-gray-700 sticky top-0 bg-gray-800">
                                <tr><th className="text-left p-1">ID</th><th className="text-left p-1">Type</th><th className="text-left p-1">Title</th></tr>
                            </thead>
                            <tbody>
                                {(contextNotifications || []).slice(0, 10).map((n, i) => (
                                    <tr key={n.id || i} className="border-b border-gray-700"><td className="p-1 text-purple-400">{n.id}</td><td className="p-1 text-yellow-400 truncate max-w-[80px]">{n.notificationType}</td><td className="p-1 text-gray-300 truncate max-w-[100px]">{n.title}</td></tr>
                                ))}
                            </tbody>
                        </table>
                        {(!contextNotifications || contextNotifications.length === 0) && <p className="text-yellow-400 p-2 text-center text-xs">Empty</p>}
                    </div>
                </div>
            </div>
            {/* Sync Status */}
            <div className="bg-gray-800 rounded p-3">
                <div className="grid grid-cols-4 gap-4 text-xs font-mono">
                    <div><span className="text-gray-400">DB:</span> <span className="text-cyan-400 font-bold">{dbNotifications.length}</span></div>
                    <div><span className="text-gray-400">FileServer:</span> <span className="text-green-400 font-bold">{fileServerNotifications.length}</span></div>
                    <div><span className="text-gray-400">LocalStorage:</span> <span className="text-orange-400 font-bold">{contextNotifications?.length || 0}</span></div>
                    <div><span className="text-gray-400">Sync:</span> <span className={fileServerNotifications.length === (contextNotifications?.length || 0) ? 'text-green-400' : 'text-red-400'}>{fileServerNotifications.length === (contextNotifications?.length || 0) ? 'OK' : 'Out of sync'}</span></div>
                </div>
            </div>
        </div>
    );

    // Render Contacts tab
    const renderContactsTab = () => (
        <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-2">
                <div className="bg-gray-800 rounded p-2 text-center">
                    <Database className="w-4 h-4 mx-auto mb-1 text-cyan-400" />
                    <div className="text-cyan-400 font-bold">{contactsData.db.length}</div>
                    <div className="text-gray-500 text-[10px]">Database</div>
                </div>
                <div className="bg-gray-800 rounded p-2 text-center">
                    <HardDrive className="w-4 h-4 mx-auto mb-1 text-green-400" />
                    <div className="text-green-400 font-bold">{contactsData.local.length}</div>
                    <div className="text-gray-500 text-[10px]">LocalStorage</div>
                </div>
                <div className="bg-gray-800 rounded p-2 text-center">
                    <Cloud className="w-4 h-4 mx-auto mb-1 text-orange-400" />
                    <div className="text-orange-400 font-bold">{contactsData.server.length}</div>
                    <div className="text-gray-500 text-[10px]">Server File</div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 flex-wrap">
                <button onClick={contactsTestLoad} disabled={contactsData.loading} className="text-xs bg-blue-700 text-white px-2 py-1 rounded hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1">
                    <Download className="w-3 h-3" /> Test Load
                </button>
                <button onClick={contactsTestSave} disabled={contactsData.loading} className="text-xs bg-purple-700 text-white px-2 py-1 rounded hover:bg-purple-600 disabled:opacity-50 flex items-center gap-1">
                    <UploadCloud className="w-3 h-3" /> Test Save
                </button>
                <button onClick={contactsFullResync} disabled={contactsData.loading} className="text-xs bg-green-700 text-white px-2 py-1 rounded hover:bg-green-600 disabled:opacity-50 flex items-center gap-1">
                    <RefreshCw className={`w-3 h-3 ${contactsData.loading ? 'animate-spin' : ''}`} /> Full Resync
                </button>
            </div>

            {/* Database Table */}
            <div className="bg-gray-800 rounded p-3">
                <h4 className="text-sm font-mono text-cyan-400 mb-2 flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    Database ({contactsData.db.length})
                </h4>
                <div className="max-h-32 overflow-auto">
                    <table className="w-full text-xs font-mono">
                        <thead className="text-cyan-400 border-b border-gray-700 sticky top-0 bg-gray-800">
                            <tr>
                                <th className="text-left p-1">#</th>
                                {CONTACTS_FIELDS.map(field => (
                                    <th key={field.key} className="text-left p-1">{field.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {contactsData.db.slice(0, 10).map((item, index) => (
                                <tr key={item.id || index} className="border-b border-gray-700 hover:bg-gray-700">
                                    <td className="p-1 text-gray-500">{index + 1}</td>
                                    {CONTACTS_FIELDS.map(field => (
                                        <td key={field.key} className={`p-1 ${field.color || 'text-gray-300'} truncate max-w-[${field.width}]`} title={String(item[field.key] || '')}>
                                            {formatCellValue(item, field)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {contactsData.db.length === 0 && <p className="text-yellow-400 p-2 text-center text-xs">No contacts in database</p>}
                </div>
            </div>

            {/* localStorage Table */}
            <div className="bg-gray-800 rounded p-3">
                <h4 className="text-sm font-mono text-green-400 mb-2 flex items-center gap-2">
                    <HardDrive className="w-4 h-4" />
                    LocalStorage ({contactsData.local.length})
                </h4>
                <div className="max-h-40 overflow-auto">
                    <table className="w-full text-xs font-mono">
                        <thead className="text-gray-400 border-b border-gray-700 sticky top-0 bg-gray-800">
                            <tr>
                                <th className="text-left p-1">#</th>
                                {CONTACTS_FIELDS.map(field => (
                                    <th key={field.key} className="text-left p-1">{field.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {contactsData.local.slice(0, 20).map((item, index) => (
                                <tr key={item.localUuid || item.id || index} className="border-b border-gray-700 hover:bg-gray-700">
                                    <td className="p-1 text-gray-500">{index + 1}</td>
                                    {CONTACTS_FIELDS.map(field => (
                                        <td key={field.key} className={`p-1 ${field.color || 'text-gray-300'} truncate max-w-[${field.width}]`} title={String(item[field.key] || '')}>
                                            {formatCellValue(item, field)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {contactsData.local.length === 0 && <p className="text-yellow-400 p-2 text-center text-xs">No contacts in localStorage</p>}
                </div>
            </div>

            {/* Server File Table */}
            <div className="bg-gray-800 rounded p-3 border-t border-gray-700">
                <h4 className="text-sm font-mono text-orange-400 mb-2 flex items-center gap-2">
                    <Cloud className="w-4 h-4" />
                    Server File (contacts.enc) - {contactsData.server.length} items
                </h4>
                <div className="max-h-40 overflow-auto">
                    <table className="w-full text-xs font-mono">
                        <thead className="text-orange-400 border-b border-gray-700 sticky top-0 bg-gray-800">
                            <tr>
                                <th className="text-left p-1">#</th>
                                {CONTACTS_FIELDS.map(field => (
                                    <th key={field.key} className="text-left p-1">{field.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {contactsData.server.slice(0, 20).map((item, index) => (
                                <tr key={item.localUuid || item.id || index} className="border-b border-gray-700 hover:bg-gray-700">
                                    <td className="p-1 text-gray-500">{index + 1}</td>
                                    {CONTACTS_FIELDS.map(field => (
                                        <td key={field.key} className={`p-1 ${field.color || 'text-gray-300'} truncate max-w-[${field.width}]`} title={String(item[field.key] || '')}>
                                            {formatCellValue(item, field)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {contactsData.server.length === 0 && <p className="text-yellow-400 p-2 text-center text-xs">No server file or empty</p>}
                </div>
            </div>

            {/* Raw JSON views */}
            <details className="bg-gray-800 rounded">
                <summary className="p-2 cursor-pointer text-gray-400 text-xs hover:text-white flex items-center gap-1">
                    <ChevronDown className="w-3 h-3" /> JSON brut localStorage ({contactsData.local.length})
                </summary>
                <pre className="p-2 text-xs text-gray-300 overflow-x-auto max-h-40">{JSON.stringify(contactsData.local, null, 2)}</pre>
            </details>
            <details className="bg-gray-800 rounded">
                <summary className="p-2 cursor-pointer text-orange-400 text-xs hover:text-white flex items-center gap-1">
                    <ChevronDown className="w-3 h-3" /> JSON brut Server File ({contactsData.server.length})
                </summary>
                <pre className="p-2 text-xs text-orange-300 overflow-x-auto max-h-40">{JSON.stringify(contactsData.server, null, 2)}</pre>
            </details>
        </div>
    );

    // Render Places tab
    const renderPlacesTab = () => (
        <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-2">
                <div className="bg-gray-800 rounded p-2 text-center">
                    <Database className="w-4 h-4 mx-auto mb-1 text-cyan-400" />
                    <div className="text-cyan-400 font-bold">{placesData.db.length}</div>
                    <div className="text-gray-500 text-[10px]">Database</div>
                </div>
                <div className="bg-gray-800 rounded p-2 text-center">
                    <HardDrive className="w-4 h-4 mx-auto mb-1 text-green-400" />
                    <div className="text-green-400 font-bold">{placesData.local.length}</div>
                    <div className="text-gray-500 text-[10px]">LocalStorage</div>
                </div>
                <div className="bg-gray-800 rounded p-2 text-center">
                    <Cloud className="w-4 h-4 mx-auto mb-1 text-orange-400" />
                    <div className="text-orange-400 font-bold">{placesData.server.length}</div>
                    <div className="text-gray-500 text-[10px]">Server File</div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 flex-wrap">
                <button onClick={placesTestLoad} disabled={placesData.loading} className="text-xs bg-blue-700 text-white px-2 py-1 rounded hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1">
                    <Download className="w-3 h-3" /> Test Load
                </button>
                <button onClick={placesTestSave} disabled={placesData.loading} className="text-xs bg-purple-700 text-white px-2 py-1 rounded hover:bg-purple-600 disabled:opacity-50 flex items-center gap-1">
                    <UploadCloud className="w-3 h-3" /> Test Save
                </button>
                <button onClick={placesFullResync} disabled={placesData.loading} className="text-xs bg-green-700 text-white px-2 py-1 rounded hover:bg-green-600 disabled:opacity-50 flex items-center gap-1">
                    <RefreshCw className={`w-3 h-3 ${placesData.loading ? 'animate-spin' : ''}`} /> Full Resync
                </button>
            </div>

            {/* Sync Debug Summary (if available) */}
            {placesData.syncDebug && (
                <div className="bg-gray-800 rounded p-3">
                    <h4 className="text-sm font-mono text-white mb-2">Sync Debug</h4>
                    <div className="grid grid-cols-5 gap-2 text-xs font-mono">
                        <div className="bg-gray-700 rounded p-2 text-center">
                            <div className="text-orange-400 font-bold">{placesData.syncDebug.dbCount || 0}</div>
                            <div className="text-gray-500 text-[10px]">DB</div>
                        </div>
                        <div className="bg-gray-700 rounded p-2 text-center">
                            <div className="text-blue-400 font-bold">{placesData.syncDebug.localCount || 0}</div>
                            <div className="text-gray-500 text-[10px]">Local</div>
                        </div>
                        <div className="bg-gray-700 rounded p-2 text-center">
                            <div className="text-purple-400 font-bold">{placesData.syncDebug.serverFileCount || 0}</div>
                            <div className="text-gray-500 text-[10px]">Server</div>
                        </div>
                        <div className="bg-gray-700 rounded p-2 text-center">
                            <div className="text-green-400 font-bold">{placesData.syncDebug.finalCount || 0}</div>
                            <div className="text-gray-500 text-[10px]">Final</div>
                        </div>
                        <div className="bg-gray-700 rounded p-2 text-center">
                            <div className="text-cyan-400 font-bold text-[10px]">{placesData.syncDebug.source || '-'}</div>
                            <div className="text-gray-500 text-[10px]">Source</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Database Table */}
            <div className="bg-gray-800 rounded p-3">
                <h4 className="text-sm font-mono text-cyan-400 mb-2 flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    Database ({placesData.db.length})
                </h4>
                <div className="max-h-32 overflow-auto">
                    <table className="w-full text-xs font-mono">
                        <thead className="text-cyan-400 border-b border-gray-700 sticky top-0 bg-gray-800">
                            <tr>
                                <th className="text-left p-1">#</th>
                                {PLACES_FIELDS.map(field => (
                                    <th key={field.key} className="text-left p-1">{field.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {placesData.db.slice(0, 10).map((item, index) => (
                                <tr key={item.id || index} className="border-b border-gray-700 hover:bg-gray-700">
                                    <td className="p-1 text-gray-500">{index + 1}</td>
                                    {PLACES_FIELDS.map(field => (
                                        <td key={field.key} className={`p-1 ${field.color || 'text-gray-300'} truncate max-w-[${field.width}]`} title={String(item[field.key] || '')}>
                                            {formatCellValue(item, field)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {placesData.db.length === 0 && <p className="text-yellow-400 p-2 text-center text-xs">No places in database</p>}
                </div>
            </div>

            {/* localStorage Table */}
            <div className="bg-gray-800 rounded p-3">
                <h4 className="text-sm font-mono text-green-400 mb-2 flex items-center gap-2">
                    <HardDrive className="w-4 h-4" />
                    LocalStorage ({placesData.local.length})
                </h4>
                <div className="max-h-40 overflow-auto">
                    <table className="w-full text-xs font-mono">
                        <thead className="text-gray-400 border-b border-gray-700 sticky top-0 bg-gray-800">
                            <tr>
                                <th className="text-left p-1">#</th>
                                {PLACES_FIELDS.map(field => (
                                    <th key={field.key} className="text-left p-1">{field.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {placesData.local.slice(0, 20).map((item, index) => (
                                <tr key={item.localUuid || item.id || index} className="border-b border-gray-700 hover:bg-gray-700">
                                    <td className="p-1 text-gray-500">{index + 1}</td>
                                    {PLACES_FIELDS.map(field => (
                                        <td key={field.key} className={`p-1 ${field.color || 'text-gray-300'} truncate max-w-[${field.width}]`} title={String(item[field.key] || '')}>
                                            {formatCellValue(item, field)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {placesData.local.length === 0 && <p className="text-yellow-400 p-2 text-center text-xs">No places in localStorage</p>}
                </div>
            </div>

            {/* Server File Table */}
            <div className="bg-gray-800 rounded p-3 border-t border-gray-700">
                <h4 className="text-sm font-mono text-orange-400 mb-2 flex items-center gap-2">
                    <Cloud className="w-4 h-4" />
                    Server File (places.enc) - {placesData.server.length} items
                </h4>
                <div className="max-h-40 overflow-auto">
                    <table className="w-full text-xs font-mono">
                        <thead className="text-orange-400 border-b border-gray-700 sticky top-0 bg-gray-800">
                            <tr>
                                <th className="text-left p-1">#</th>
                                {PLACES_FIELDS.map(field => (
                                    <th key={field.key} className="text-left p-1">{field.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {placesData.server.slice(0, 20).map((item, index) => (
                                <tr key={item.localUuid || item.id || index} className="border-b border-gray-700 hover:bg-gray-700">
                                    <td className="p-1 text-gray-500">{index + 1}</td>
                                    {PLACES_FIELDS.map(field => (
                                        <td key={field.key} className={`p-1 ${field.color || 'text-gray-300'} truncate max-w-[${field.width}]`} title={String(item[field.key] || '')}>
                                            {formatCellValue(item, field)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {placesData.server.length === 0 && <p className="text-yellow-400 p-2 text-center text-xs">No server file or empty</p>}
                </div>
            </div>

            {/* Raw JSON views */}
            <details className="bg-gray-800 rounded">
                <summary className="p-2 cursor-pointer text-gray-400 text-xs hover:text-white flex items-center gap-1">
                    <ChevronDown className="w-3 h-3" /> JSON brut localStorage ({placesData.local.length})
                </summary>
                <pre className="p-2 text-xs text-gray-300 overflow-x-auto max-h-40">{JSON.stringify(placesData.local, null, 2)}</pre>
            </details>
            <details className="bg-gray-800 rounded">
                <summary className="p-2 cursor-pointer text-orange-400 text-xs hover:text-white flex items-center gap-1">
                    <ChevronDown className="w-3 h-3" /> JSON brut Server File ({placesData.server.length})
                </summary>
                <pre className="p-2 text-xs text-orange-300 overflow-x-auto max-h-40">{JSON.stringify(placesData.server, null, 2)}</pre>
            </details>
        </div>
    );

    // Render Meetings tab
    const renderMeetingsTab = () => (
        <div className="space-y-4">
            <div className="bg-gray-800 rounded p-3">
                <h4 className="text-sm font-mono text-rose-400 mb-2 flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    Database ({dbMeetings.length})
                </h4>
                <div className="max-h-60 overflow-y-auto">
                    <table className="w-full text-xs font-mono">
                        <thead className="text-gray-400 border-b border-gray-700 sticky top-0 bg-gray-800">
                            <tr><th className="text-left p-1">ID</th><th className="text-left p-1">Title</th><th className="text-left p-1">Status</th><th className="text-left p-1">Date</th><th className="text-left p-1">Organizer</th></tr>
                        </thead>
                        <tbody>
                            {dbMeetings.map((m, i) => (
                                <tr key={m.id || i} className="border-b border-gray-700">
                                    <td className="p-1 text-purple-400">{m.id}</td>
                                    <td className="p-1 text-gray-300 truncate max-w-[150px]">{m.title}</td>
                                    <td className="p-1"><span className={`px-1 rounded text-xs ${m.status === 'CONFIRMED' ? 'bg-green-900 text-green-300' : m.status === 'PENDING' ? 'bg-yellow-900 text-yellow-300' : 'bg-gray-700 text-gray-300'}`}>{m.status}</span></td>
                                    <td className="p-1 text-gray-500">{formatDate(m.meetingDate)}</td>
                                    <td className="p-1 text-cyan-400">{m.organizerName || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {dbMeetings.length === 0 && <p className="text-yellow-400 p-2 text-center text-xs">No meetings</p>}
                </div>
            </div>
        </div>
    );

    // Render generic module tab
    const renderGenericTab = (tabId) => (
        <div className="bg-gray-800 rounded p-4">
            <h4 className="text-sm font-mono text-white mb-2">Module: {tabId.toUpperCase()}</h4>
            <p className="text-gray-400 text-xs">Debug view not implemented for this module yet.</p>
        </div>
    );

    // Render tab content based on module code
    const renderTabContent = () => {
        const tabCode = activeTab?.toUpperCase();

        switch (tabCode) {
            case 'GENERAL': return renderGeneralTab();
            case 'MODULES': return renderModulesTab();
            case 'NOTIFICATIONS': return renderNotificationsTab();
            case 'CONTACTS': return renderContactsTab();
            case 'PLACES': return renderPlacesTab();
            case 'MEETINGS': return renderMeetingsTab();
            default: return renderGenericTab(activeTab);
        }
    };

    // Don't render if not admin
    if (!isAdmin) return null;

    return (
        <>
            {/* Toggle Button - Fixed bottom right, respects sidebar */}
            <div className="fixed bottom-4 right-4 z-[100] hidden sm:block">
                <button
                    onClick={handleToggle}
                    className={`p-3 ${isOpen ? 'bg-red-700' : 'bg-gray-800'} text-white rounded-full shadow-lg hover:opacity-80 transition-all`}
                    title="Debug Panel"
                >
                    {isOpen ? <X className="w-5 h-5" /> : <Bug className="w-5 h-5" />}
                </button>
            </div>

            {/* Bottom Drawer Panel - respects sidebar space */}
            {isOpen && (
                <div
                    className={`fixed bottom-0 left-0 sm:left-20 right-0 z-[100] bg-gray-900 border-t border-gray-700 shadow-2xl ${isDragging ? '' : 'transition-all duration-300'}`}
                    style={{ height: isMinimized ? '48px' : `${panelHeight}vh` }}
                >
                    {/* Resize Handle */}
                    <div
                        className="absolute top-0 left-0 right-0 h-2 cursor-ns-resize bg-gray-700 hover:bg-blue-600 transition-colors flex items-center justify-center"
                        onMouseDown={handleMouseDown}
                        onTouchStart={handleTouchStart}
                    >
                        <div className="w-12 h-1 bg-gray-500 rounded-full" />
                    </div>

                    {/* Header */}
                    <div className="h-12 mt-2 px-4 bg-gray-800 border-b border-gray-700 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <Bug className="w-4 h-4 text-green-400" />
                            <span className="font-mono text-white text-sm">Debug Panel</span>
                            <span className="text-xs text-gray-500">({modules.length} modules)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={refreshAll} disabled={loading} className="text-xs bg-blue-700 text-white px-2 py-1 rounded hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1">
                                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
                            </button>
                            <button onClick={() => setIsMinimized(!isMinimized)} className="text-gray-400 hover:text-white p-1">
                                {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white p-1">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Content (hidden when minimized) */}
                    {!isMinimized && (
                        <div className="flex" style={{ height: `calc(${panelHeight}vh - 56px)` }}>
                            {/* Tabs Sidebar */}
                            <div className="w-40 bg-gray-800 border-r border-gray-700 overflow-y-auto">
                                {allTabs.map(tab => {
                                    const Icon = tab.icon;
                                    const isActive = activeTab === tab.id;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-mono transition-colors ${isActive ? 'text-white bg-gray-700 border-l-2 border-blue-500' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}
                                        >
                                            <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? tab.color : ''}`} />
                                            <span className="truncate">{tab.label}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Tab Content */}
                            <div className="flex-1 overflow-y-auto p-4">
                                {/* Debug Logs */}
                                {debugLogs.length > 0 && (
                                    <div className="mb-4 p-2 bg-gray-800 rounded text-xs font-mono">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-gray-400">Logs</span>
                                            <button onClick={() => setDebugLogs([])} className="text-gray-500 hover:text-white">Clear</button>
                                        </div>
                                        <div className="max-h-16 overflow-y-auto space-y-0.5">
                                            {debugLogs.slice(-5).map((log, i) => (
                                                <div key={i} className={log.type === 'error' ? 'text-red-400' : log.type === 'success' ? 'text-green-400' : 'text-gray-300'}>
                                                    [{log.time}] {log.message}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Active Tab Content */}
                                {renderTabContent()}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default DebugPanelTabs;
